import { ParamMap } from '@angular/router';
import {
  ACCOUNT_PAYABLE_MENU,
  CompanyPageSubMenu,
  CompanyPermission,
} from '../models/company-permission.model';
import { RoleConstant } from '../models/role-constant';
import { ServiceRequestStatus } from '../models/service-request-status';
import {
  getCompanyPermissionForCompany,
  getPageUrlQueryParams,
  isApSubMenuRoute,
  isApSubMenuRouteActive,
  normalizeRoutePath,
  parsePageUrlParts,
} from './company-permission.util';

/** Query keys used by BE for AP list tabs (`accounts-payable/list?{key}=`). */
export const AP_LIST_TAB_QUERY_KEYS = [
  'overview',
  'approval',
  'payment',
  'draft',
  'allpayments',
  'bills',
] as const;

export type ApListTabQueryKey = (typeof AP_LIST_TAB_QUERY_KEYS)[number];

/** Horizontal tab order on AP Creator list (Figma Accounts Payable revamp). */
export const FIGMA_AP_LIST_TAB_ORDER: readonly ApListTabQueryKey[] = [
  'overview',
  'draft',
  'approval',
  'payment',
  'bills',
  'allpayments',
];

export const AP_LIST_TAB_DISPLAY_LABELS: Partial<
  Record<ApListTabQueryKey, string>
> = {
  overview: 'Overview',
  draft: 'Drafts',
  approval: 'Pending approval',
  payment: 'Pending payment',
  bills: 'All Bills',
  allpayments: 'All Payments',
};

export interface ApListTabViewModel {
  page: string;
  pageUrl: string;
  queryParams: Record<string, string>;
  queryKey: ApListTabQueryKey | null;
}

/** BE search filter key for Overview tab (`AccountPayableFilters.OverviewStatus`). */
export const AP_OVERVIEW_STATUS_FILTER_KEY = 'OverviewStatus';

/** BE search filter key for Draft / Pending approval / Pending payment tabs. */
export const AP_STATUS_FILTER_KEY = 'status';

/** AP Creator overview tab list page size (Figma / product). */
export const AP_OVERVIEW_TAB_PAGE_SIZE = 8;

/** Default API page size for Drafts and other non-overview AP list tabs. */
export const AP_LIST_DEFAULT_PAGE_SIZE = 10;

export type ApListTabPreset = {
  mode: 'overview' | 'table';
  filters?: Record<string, unknown>;
  pageSize?: number;
};

export function apOverviewStatusFilter(
  statuses: ServiceRequestStatus[]
): Record<string, string[]> {
  return { [AP_OVERVIEW_STATUS_FILTER_KEY]: statuses };
}

/** AP Manager 2, Company Admin, and AP Manager 1 (same Overview as Manager 2). */
export function isApOverviewWorkItemsRole(
  roleId: string | null | undefined,
  _bypassApManager1 = false
): boolean {
  if (!roleId) {
    return false;
  }

  return (
    roleId === RoleConstant.accountsPayableManager2 ||
    roleId === RoleConstant.companyAdminId ||
    roleId === RoleConstant.accountsPayableManager1
  );
}

/** Company Admin Overview shows Needs approval only (no Ready for payment). */
export function isApOverviewApprovalOnlyRole(
  roleId: string | null | undefined
): boolean {
  return roleId === RoleConstant.companyAdminId;
}

/** Overview tab `OverviewStatus` filter for the active role. */
export function resolveApOverviewTabFilters(
  roleId: string | null | undefined,
  bypassApManager1 = false
): Record<string, string[]> {
  if (isApOverviewApprovalOnlyRole(roleId)) {
    return apOverviewStatusFilter([ServiceRequestStatus.pendingApproval]);
  }

  if (isApOverviewWorkItemsRole(roleId, bypassApManager1)) {
    return apOverviewStatusFilter([
      ServiceRequestStatus.pendingApproval,
      ServiceRequestStatus.paymentPending,
    ]);
  }

  return apOverviewStatusFilter([ServiceRequestStatus.draft]);
}

/** Tab-scoped `status` — single string (same shape as filter-panel status). */
export function apStatusFilter(
  status: ServiceRequestStatus
): Record<string, string> {
  return { [AP_STATUS_FILTER_KEY]: status };
}

export function isApListTabPageUrl(pageUrl: string): boolean {
  if (!isApSubMenuRoute(pageUrl)) {
    return false;
  }

  const { path, queryParams } = parsePageUrlParts(pageUrl);
  if (path.toLowerCase() !== 'accounts-payable/list') {
    return false;
  }

  if (!queryParams) {
    return false;
  }

  return AP_LIST_TAB_QUERY_KEYS.some((key) =>
    Object.prototype.hasOwnProperty.call(queryParams, key)
  );
}

/** List horizontal tabs from the singular **Account Payable** menu (BE order). */
export function getAccountPayableListTabSubMenus(
  companyPermission: CompanyPermission | null | undefined
): CompanyPageSubMenu[] {
  const pagePermission = companyPermission?.pagePermissions?.find(
    (entry) => entry.menu === ACCOUNT_PAYABLE_MENU
  );

  return (pagePermission?.subMenus ?? []).filter((subMenu) =>
    isApListTabPageUrl(subMenu.pageUrl)
  );
}

export function getAccountPayableListTabs(
  storedCompanyPermissions: CompanyPermission[] | null | undefined,
  companyId: string | null | undefined
): ApListTabViewModel[] {
  const companyPermission = getCompanyPermissionForCompany(
    storedCompanyPermissions,
    companyId
  );

  return getAccountPayableListTabSubMenus(companyPermission).map((subMenu) => {
    const queryParams = getPageUrlQueryParams(subMenu.pageUrl) ?? {};
    const queryKey =
      AP_LIST_TAB_QUERY_KEYS.find((key) =>
        Object.prototype.hasOwnProperty.call(queryParams, key)
      ) ?? null;

    return {
      page: subMenu.page,
      pageUrl: subMenu.pageUrl,
      queryParams,
      queryKey,
    };
  });
}

/** Sort BE tabs and apply revamp display labels. */
export function sortApListTabsForDesign(
  tabs: ApListTabViewModel[]
): ApListTabViewModel[] {
  return [...tabs]
    .sort((a, b) => {
      const ai = a.queryKey
        ? FIGMA_AP_LIST_TAB_ORDER.indexOf(a.queryKey)
        : -1;
      const bi = b.queryKey
        ? FIGMA_AP_LIST_TAB_ORDER.indexOf(b.queryKey)
        : -1;
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    })
    .map((tab) => {
      const label =
        tab.queryKey && AP_LIST_TAB_DISPLAY_LABELS[tab.queryKey]
          ? AP_LIST_TAB_DISPLAY_LABELS[tab.queryKey]!
          : tab.page;
      return { ...tab, page: label };
    });
}

/**
 * Sort AP sidebar subMenus: list-tab items in Figma tab order (with display
 * labels); non-list items keep relative order after list tabs.
 */
export function sortApNavSubMenusForDesign(
  subMenus: CompanyPageSubMenu[]
): CompanyPageSubMenu[] {
  const decorated = (subMenus ?? []).map((subMenu, index) => {
    const queryParams = getPageUrlQueryParams(subMenu.pageUrl) ?? {};
    const queryKey =
      AP_LIST_TAB_QUERY_KEYS.find((key) =>
        Object.prototype.hasOwnProperty.call(queryParams, key)
      ) ?? null;
    const order = queryKey
      ? FIGMA_AP_LIST_TAB_ORDER.indexOf(queryKey)
      : -1;
    const page =
      queryKey && AP_LIST_TAB_DISPLAY_LABELS[queryKey]
        ? AP_LIST_TAB_DISPLAY_LABELS[queryKey]!
        : subMenu.page;
    return { subMenu: { ...subMenu, page }, order, index };
  });

  return decorated
    .sort((a, b) => {
      const ai = a.order === -1 ? 1000 + a.index : a.order;
      const bi = b.order === -1 ? 1000 + b.index : b.order;
      return ai - bi;
    })
    .map((entry) => entry.subMenu);
}

export function resolveApListTabQueryKey(
  query: ParamMap | URLSearchParams | null | undefined
): ApListTabQueryKey | null {
  if (!query) {
    return null;
  }

  const hasKey = (key: string) =>
    query instanceof URLSearchParams
      ? query.has(key)
      : query.has(key);

  return AP_LIST_TAB_QUERY_KEYS.find((key) => hasKey(key)) ?? null;
}

export function resolveApListTabPreset(
  query: ParamMap | URLSearchParams | null | undefined,
  selectedRoleId?: string | null,
  bypassApManager1 = false
): ApListTabPreset {
  const tabKey = resolveApListTabQueryKey(query);

  switch (tabKey) {
    case 'overview':
      return {
        mode: 'overview',
        filters: resolveApOverviewTabFilters(selectedRoleId, bypassApManager1),
        pageSize: AP_OVERVIEW_TAB_PAGE_SIZE,
      };
    case 'draft':
      return {
        mode: 'table',
        filters: apStatusFilter(ServiceRequestStatus.draft),
      };
    case 'approval':
      return {
        mode: 'table',
        filters: apStatusFilter(ServiceRequestStatus.pendingApproval),
      };
    case 'payment':
      return {
        mode: 'table',
        filters: apStatusFilter(ServiceRequestStatus.paymentPending),
      };
    case 'allpayments':
    case 'bills':
      return { mode: 'table' };
    default:
      return { mode: 'table' };
  }
}

export function isApListTabActive(
  tab: ApListTabViewModel,
  pathname: string,
  query: URLSearchParams | ParamMap | null | undefined
): boolean {
  const path = normalizeRoutePath(pathname);
  if (path !== 'accounts-payable/list' && path !== 'accounts-payable') {
    return false;
  }

  if (!tab.queryKey || !query) {
    return false;
  }

  return query instanceof URLSearchParams
    ? query.has(tab.queryKey)
    : query.has(tab.queryKey);
}

export function isApListTabUrlAllowed(
  url: string,
  subMenus: CompanyPageSubMenu[] | null | undefined
): boolean {
  const path = normalizeRoutePath(url.split('?')[0]);
  if (path !== 'accounts-payable/list' && path !== 'accounts-payable') {
    return true;
  }

  const queryString = url.includes('?') ? url.split('?')[1].split('#')[0] : '';
  const query = new URLSearchParams(queryString);
  const tabKey = resolveApListTabQueryKey(query);

  if (!tabKey) {
    return true;
  }

  const menus = subMenus ?? [];
  return menus.some((subMenu) =>
    isApSubMenuRouteActive(subMenu.pageUrl, path, query)
  );
}

/**
 * BE saved-filter `tab` key (PascalCase) — distinct from URL query keys
 * (`approval=`, `payment=`, etc.).
 */
export function resolveApSavedFilterTabKey(
  tabKey?: ApListTabQueryKey | null
): string {
  if (!tabKey) {
    return 'list';
  }

  switch (tabKey) {
    case 'overview':
      return 'Overview';
    case 'draft':
      return 'Draft';
    case 'approval':
      return 'PendingApproval';
    case 'payment':
      return 'PendingPayment';
    case 'bills':
      return 'AllBills';
    case 'allpayments':
      return 'AllPayments';
    default:
      return tabKey;
  }
}
